/**
 * Shared role names, hierarchy, and access helpers for web/desktop auth.
 *
 * Admin implies Licensed so back-office users inherit the licensed portal
 * without needing a second role or a license attached to the account.
 */

/** Canonical role names stored in Prisma `Role.name`. */
export const ROLE_NAME = {
  Admin: "Admin",
  Licensed: "Licensed",
  Speaker: "Speaker",
  DisplayEditor: "DisplayEditor",
  Demo: "Demo",
} as const;

export type RoleName = (typeof ROLE_NAME)[keyof typeof ROLE_NAME];

/**
 * Roles implied by each assigned role.
 * Admin includes Licensed so `@Roles(Licensed)` and portal checks accept Admins.
 */
export const ROLE_IMPLIES: Record<RoleName, readonly RoleName[]> = {
  Admin: [ROLE_NAME.Admin, ROLE_NAME.Licensed],
  Licensed: [ROLE_NAME.Licensed],
  Speaker: [ROLE_NAME.Speaker],
  DisplayEditor: [ROLE_NAME.DisplayEditor],
  Demo: [ROLE_NAME.Demo],
};

/** License summary used for Speaker M2M / Licensed own-license checks. */
export type LicenseKeyBearing = {
  id?: string;
  key: string;
  name?: string | null;
};

/** Minimal shape for role checks (Prisma User + userRoles or JWT payload). */
export type RoleBearingUser = {
  id: string;
  parentUserId?: string | null;
  userRoles?: Array<{ role: { name: string } }>;
  roles?: string[];
  /** Own license (Licensed) or hydrated parent license (DisplayEditor). */
  license?: LicenseKeyBearing | null;
  /** Speaker ↔ 0..N licenses (join rows or flattened licenses). */
  speakerLicenses?: Array<{ license: LicenseKeyBearing } | LicenseKeyBearing>;
};

/** Sentinel returned by {@link getAccessibleLicenseKeys} for Admin (all meets). */
export const ACCESS_ALL_LICENSE_KEYS = "*" as const;

/**
 * Normalizes a role string to a known {@link RoleName}, or null if unknown.
 */
export const normalizeRoleName = (name: string): RoleName | null => {
  const match = (Object.values(ROLE_NAME) as string[]).find(
    (role) => role.toLowerCase() === name.toLowerCase(),
  );
  return (match as RoleName | undefined) ?? null;
};

/**
 * Collects role names from either Prisma `userRoles` or a flat `roles` array.
 */
export const getAssignedRoleNames = (
  user: RoleBearingUser | null | undefined,
): RoleName[] => {
  if (!user) {
    return [];
  }
  if (user.userRoles?.length) {
    return user.userRoles
      .map((relation) => normalizeRoleName(relation.role.name))
      .filter((name): name is RoleName => name !== null);
  }
  if (user.roles?.length) {
    return user.roles
      .map((name) => normalizeRoleName(name))
      .filter((name): name is RoleName => name !== null);
  }
  return [];
};

/**
 * Returns true when the user holds a role that implies {@link required}.
 *
 * @example
 * userHasRole(adminUser, ROLE_NAME.Licensed) // true via Admin hierarchy
 */
export const userHasRole = (
  user: RoleBearingUser | null | undefined,
  required: RoleName | string,
): boolean => {
  const requiredNormalized = normalizeRoleName(
    typeof required === "string" ? required : required,
  );
  if (!requiredNormalized) {
    return false;
  }
  const assigned = getAssignedRoleNames(user);
  return assigned.some((role) =>
    ROLE_IMPLIES[role].includes(requiredNormalized),
  );
};

/**
 * Layouts/themes/assets owner: DisplayEditor acts as their Licensed parent.
 */
export const getEffectiveLayoutOwnerUserId = (
  user: RoleBearingUser | null | undefined,
): string | null => {
  if (!user?.id) {
    return null;
  }
  if (
    userHasRole(user, ROLE_NAME.DisplayEditor) &&
    user.parentUserId &&
    user.parentUserId.length > 0
  ) {
    return user.parentUserId;
  }
  return user.id;
};

/**
 * Speaker and DisplayEditor are web-only and must not connect as DESKTOP.
 */
export const isWebOnlyRestrictedRole = (
  user: RoleBearingUser | null | undefined,
): boolean =>
  userHasRole(user, ROLE_NAME.Speaker) ||
  userHasRole(user, ROLE_NAME.DisplayEditor);

/**
 * Users that may authenticate on WEB without their own license.
 */
export const canSignInWithoutOwnLicense = (
  user: RoleBearingUser | null | undefined,
): boolean =>
  userHasRole(user, ROLE_NAME.Admin) ||
  userHasRole(user, ROLE_NAME.Speaker) ||
  userHasRole(user, ROLE_NAME.DisplayEditor);

/**
 * Post-login landing path by highest-priority role.
 * Admin wins over Licensed; restricted roles get their dedicated home.
 */
export const resolvePostLoginPath = (
  user: RoleBearingUser | null | undefined,
): string => {
  if (userHasRole(user, ROLE_NAME.Admin)) {
    return "/admin";
  }
  if (userHasRole(user, ROLE_NAME.Licensed)) {
    return "/licensed";
  }
  if (userHasRole(user, ROLE_NAME.DisplayEditor)) {
    return "/licensed/layouts";
  }
  if (userHasRole(user, ROLE_NAME.Speaker)) {
    return "/speaker";
  }
  return "/sign-in";
};

/**
 * Extracts license keys from speakerLicenses join rows or flat license objects.
 */
const collectSpeakerLicenseKeys = (
  user: RoleBearingUser | null | undefined,
): string[] => {
  if (!user?.speakerLicenses?.length) {
    return [];
  }
  const keys: string[] = [];
  for (const entry of user.speakerLicenses) {
    const key =
      "license" in entry && entry.license
        ? entry.license.key
        : "key" in entry
          ? entry.key
          : undefined;
    if (key && key.length > 0) {
      keys.push(key);
    }
  }
  return keys;
};

/**
 * License keys the user may use for public live meets and connected speaker features.
 *
 * - Admin → `[ACCESS_ALL_LICENSE_KEYS]` (`"*"`)
 * - Licensed (incl. via Admin hierarchy only when Admin is already handled) → own `license.key`
 * - Speaker → keys from `speakerLicenses`
 * - Others → `[]`
 */
export const getAccessibleLicenseKeys = (
  user: RoleBearingUser | null | undefined,
): string[] => {
  if (!user) {
    return [];
  }
  if (userHasRole(user, ROLE_NAME.Admin)) {
    return [ACCESS_ALL_LICENSE_KEYS];
  }
  if (userHasRole(user, ROLE_NAME.Licensed)) {
    const ownKey = user.license?.key;
    return ownKey && ownKey.length > 0 ? [ownKey] : [];
  }
  if (userHasRole(user, ROLE_NAME.Speaker)) {
    return collectSpeakerLicenseKeys(user);
  }
  return [];
};

/**
 * Whether the user may see / use connected features on a meet identified by licenseKey.
 *
 * @param user - Authenticated user with roles and optional license links.
 * @param meetLicenseKey - `Meet.licenseKey` from live store or DB.
 */
export const canAccessMeetByLicense = (
  user: RoleBearingUser | null | undefined,
  meetLicenseKey: string | null | undefined,
): boolean => {
  if (!user || !meetLicenseKey || meetLicenseKey.length === 0) {
    return false;
  }
  const keys = getAccessibleLicenseKeys(user);
  if (keys.includes(ACCESS_ALL_LICENSE_KEYS)) {
    return true;
  }
  return keys.includes(meetLicenseKey);
};
